/**
 * The inquiry routes, in the shape `src/routes.extra.ts` documented as the seam:
 *
 *     { path: 'inquiries',            element: <InquiriesListPage /> }
 *     { path: 'inquiries/:inquiryId', element: <InquiryRecordPage /> }
 *
 * Both sit inside a provider layout route rather than each mounting their own, so a toast
 * raised on the list SURVIVES navigation to the record. That matters for exactly one
 * feature: Undo. A stage change made from the table, followed by a click into the record,
 * must not silently drop the Undo the operator was about to press — which is what a
 * per-page provider would do when the page unmounted.
 *
 * The layout deliberately does nothing else. Country scoping, the shell and session
 * resolution are already guaranteed by the parent routes (`RequireCountry` → `AppShell`).
 */

import { Outlet } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { ToastProvider } from './toast';
import { InquiriesListPage } from './InquiriesListPage';
import { InquiryRecordPage } from './InquiryRecordPage';
import { InquiryCreatePage } from './InquiryCreatePage';
import { FollowupPage } from './FollowupPage';

function InquiriesProviders(): React.JSX.Element {
  return (
    <ToastProvider>
      <Outlet />
    </ToastProvider>
  );
}

/*
 * WEB TIER A · WT5 — two routes grew out of the slide-over kill. `inquiries/new`
 * is the create page (forms.html screen 1; was the `?panel=new` slide-over)
 * and `inquiries/:inquiryId/follow-up` is the follow-up page (was the
 * `FollowupPanel`, opened from the row menu and the record). Both sit inside
 * the SAME provider layout, and now a THIRD flow depends on that: the
 * follow-up page fires an optional stage move and then navigates to the
 * record — the move's toast, and the Undo it carries, must survive that
 * navigation exactly as the list→record case documented above.
 */
export const inquiriesRoutes: RouteObject[] = [
  {
    element: <InquiriesProviders />,
    children: [
      { path: 'inquiries', element: <InquiriesListPage /> },
      { path: 'inquiries/new', element: <InquiryCreatePage /> },
      { path: 'inquiries/:inquiryId', element: <InquiryRecordPage /> },
      { path: 'inquiries/:inquiryId/follow-up', element: <FollowupPage /> },
    ],
  },
];
